import { type ReactNode, useEffect, useState } from "react";

import { ZoomablePhoto } from "@/components/media/photo-zoomable";
import { fetchArtifactBlob } from "@/lib/tina/artifact-image";

export interface GcsImageProps {
  /** Pipeline event id — scopes the artifact fetch on the orchestrator. */
  eventId: string;
  /** gs:// ref of the stored artifact (preferred source). */
  artifactRef?: string;
  /** Original source URL, used only when there is no ref or the fetch fails. */
  fallbackUrl?: string;
  alt?: string;
  className?: string;
  /**
   * What to render when the artifact fetch **failed** and there is no
   * `fallbackUrl` left to try (owner ruling R76).
   *
   * Opt-in, and deliberately not shown while the fetch is still in flight: the
   * default of rendering nothing is right for a strip of thumbnails where a
   * missing one is unremarkable, and wrong for a surface where "no image was
   * offered" and "the image we have could not be loaded" are different answers
   * to the curator's question. `refresh_review`'s poster comparison is the
   * second kind — a candidate that 403s left a gap under a "Proposed" label,
   * which reads as "the page offered nothing".
   */
  fallback?: ReactNode;
}

/**
 * Resolve a fetch-stage image to a renderable object URL, fetching the stored
 * GCS blob through the orchestrator. Returns `undefined` while loading and
 * `failed=true` if the fetch errors. Revokes the object URL on unmount / ref
 * change so blobs don't leak.
 */
function useArtifactObjectUrl(
  eventId: string,
  artifactRef: string | undefined,
): { objectUrl?: string; failed: boolean } {
  const [objectUrl, setObjectUrl] = useState<string>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setObjectUrl(undefined);
    setFailed(false);
    if (!eventId || !artifactRef) return;

    let created: string | undefined;
    let cancelled = false;
    const controller = new AbortController();

    fetchArtifactBlob(eventId, artifactRef, controller.signal)
      .then((blob) => {
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setObjectUrl(created);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (created) URL.revokeObjectURL(created);
    };
  }, [eventId, artifactRef]);

  return { objectUrl, failed };
}

/**
 * Render a Tina pipeline image, preferring the stored GCS blob (streamed
 * through the orchestrator, shown via a blob: object URL) over the candidate's
 * original source URL. Hotlinking the source breaks for hosts that block
 * cross-origin embedding (e.g. Instagram's `Cross-Origin-Resource-Policy:
 * same-origin`), which renders as a broken thumbnail. Falls back to the source
 * URL only when there is no GCS ref or the blob fetch fails; renders nothing
 * while a ref is still loading (avoids a broken-source flash).
 *
 * With nothing left to render there are two states, and a caller can tell them
 * apart: `fallback` — when given — stands in for a fetch that **failed**, while
 * a ref that is merely still loading, or absent with no `fallbackUrl`, still
 * renders nothing. See {@link GcsImageProps.fallback}.
 */
export function GcsImage({
  eventId,
  artifactRef,
  fallbackUrl,
  alt,
  className,
  fallback,
}: GcsImageProps) {
  const { objectUrl, failed } = useArtifactObjectUrl(eventId, artifactRef);

  const src = artifactRef && !failed ? objectUrl : fallbackUrl;
  if (!src) return failed ? <>{fallback ?? null}</> : null;

  return <ZoomablePhoto url={src} alt={alt} className={className} />;
}
